import { FormEvent, useState } from "react";
import {
  changeField,
  FormValue,
  FormValues,
  missingRequired,
  toRequest,
  visibleFields,
} from "./skillForm";
import { SkillField, SkillOption } from "./types";

const toggle = (values: string[], value: string, on: boolean) =>
  on ? [...values, value] : values.filter((item) => item !== value);

// Een keuze met weinig opties als rondjes, met veel als uitklaplijst.
const FEW_OPTIONS = 4;

const Choice = ({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: SkillOption[];
  value: string;
  onChange: (value: string) => void;
}) =>
  options.length <= FEW_OPTIONS ? (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {options.map((option) => (
        <label key={option.value} className="flex items-center gap-1.5">
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  ) : (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded border border-gray-300 px-1.5 py-1"
    >
      <option value="">—</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

// Meerdere keuzes als knopjes die aan en uit gaan: een lijst met twintig
// grondsoorten past zo op een paar regels.
const Chips = ({
  options,
  value,
  onChange,
}: {
  options: SkillOption[];
  value: string[];
  onChange: (value: string[]) => void;
}) => (
  <div className="flex flex-wrap gap-1">
    {options.map((option) => {
      const on = value.includes(option.value);
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={on}
          onClick={() => onChange(toggle(value, option.value, !on))}
          className={`rounded-full border px-2 py-0.5 ${
            on ? "border-dark-green bg-yellow/40" : "border-gray-300 bg-white hover:bg-gray-50"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

// Ook in de skilleditor, voor een voorgevulde of vaste waarde.
export const FieldInput = ({
  field,
  value,
  parcels,
  onChange,
}: {
  field: SkillField;
  value: FormValue;
  parcels: SkillOption[];
  onChange: (value: FormValue) => void;
}) => {
  switch (field.type) {
    case "parcel":
      return (
        <Choice name={field.name} options={parcels} value={value as string} onChange={onChange} />
      );
    case "parcels":
      return <Chips options={parcels} value={value as string[]} onChange={onChange} />;
    case "choice":
      return (
        <Choice
          name={field.name}
          options={field.options}
          value={value as string}
          onChange={onChange}
        />
      );
    case "multi_choice":
      return <Chips options={field.options} value={value as string[]} onChange={onChange} />;
    case "range": {
      const range = value as { min: string; max: string };
      const bound = (key: "min" | "max", placeholder: string) => (
        <input
          type="number"
          value={range[key]}
          placeholder={placeholder}
          onChange={(event) => onChange({ ...range, [key]: event.target.value })}
          className="w-24 rounded border border-gray-300 px-1.5 py-1"
        />
      );
      return (
        <div className="flex items-center gap-1.5">
          {bound("min", "van")}–{bound("max", "tot")}
        </div>
      );
    }
    default:
      // Lange tekst (een situatieschets, een onderzoeksvraag) krijgt ruimte.
      return field.maxLength > 200 ? (
        <textarea
          value={value as string}
          maxLength={field.maxLength}
          rows={2}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded border border-gray-300 p-1.5"
        />
      ) : (
        <input
          value={value as string}
          maxLength={field.maxLength}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded border border-gray-300 px-1.5 py-1"
        />
      );
  }
};

/**
 * Het formulier van een skill: in het gesprek (een aangeboden skill) en boven
 * het invoerveld (een skill uit het menu). `onSubmit` geeft de fouten van de
 * server terug, of niets als het gelukt is.
 */
export const SkillForm = ({
  fields,
  initial,
  parcels,
  submitLabel = "Start",
  onSubmit,
  onCancel,
  disabled = false,
}: {
  fields: SkillField[];
  initial: FormValues;
  parcels: SkillOption[];
  submitLabel?: string;
  onSubmit: (values: Record<string, any>) => Promise<string[] | undefined>;
  onCancel?: () => void;
  disabled?: boolean;
}) => {
  const [form, setForm] = useState<FormValues>(initial);
  // Vastgelegd bij het openen: zie visibleFields.
  const [initiallyShown] = useState(() => visibleFields(fields, initial));
  const [showAll, setShowAll] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const shown = showAll ? fields : initiallyShown;
  const missing = missingRequired(fields, form);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrors((await onSubmit(toRequest(fields, form))) ?? []);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-2 text-xs">
      {shown.map((field) => (
        <div key={field.name}>
          <p className="font-semibold">
            {field.label}
            {field.required && <span className="text-red-600"> *</span>}
          </p>
          <div className="mt-0.5">
            <FieldInput
              field={field}
              value={form[field.name]}
              parcels={parcels}
              onChange={(value) => setForm(changeField(fields, form, field, value))}
            />
          </div>
        </div>
      ))}
      {shown.length < fields.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-dark-green underline"
        >
          Meer velden ({fields.length - shown.length})
        </button>
      )}
      {errors.length > 0 && (
        <ul className="list-disc pl-4 text-red-600">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={disabled || busy || missing.length > 0}
          title={missing.length ? `Nog in te vullen: ${missing.join(", ")}` : undefined}
          className="rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow disabled:opacity-50"
        >
          {busy ? "Starten…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-2 py-1 text-gray-500">
            Annuleren
          </button>
        )}
      </div>
    </form>
  );
};
